import React, { useCallback, useEffect, useState } from 'react';
import { Headphones, Plus, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import {
  createTicket,
  getSLAPolicy,
  getSupportMetrics,
  listKBArticles,
  listTickets,
  updateTicket,
  type KBArticle,
  type SLAPolicy,
  type SupportMetrics,
  type SupportTicket,
} from '../services/supportApi';

const STATUS_LABELS: Record<string, string> = {
  open: 'Aberto',
  in_progress: 'Em andamento',
  waiting: 'Aguardando',
  resolved: 'Resolvido',
  closed: 'Fechado',
};

const Suporte: React.FC = () => {
  const { t } = useI18n();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'supervisor';
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [metrics, setMetrics] = useState<SupportMetrics | null>(null);
  const [sla, setSla] = useState<SLAPolicy | null>(null);
  const [articles, setArticles] = useState<KBArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [t, m, s, kb] = await Promise.all([
        listTickets(),
        getSupportMetrics(),
        getSLAPolicy(),
        listKBArticles(),
      ]);
      setTickets(t);
      setMetrics(m);
      setSla(s);
      setArticles(kb);
    } catch (e) {
      console.error(e);
      setError('Não foi possível carregar o suporte. Verifique se o auth-service está ativo.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    try {
      await createTicket({ title: title.trim(), description: description.trim() || undefined, priority });
      setTitle('');
      setDescription('');
      await load();
    } catch {
      setError(t('support.errorCreate'));
    }
  };

  const handleStatusChange = async (id: string, status: string) => {
    try {
      await updateTicket(id, { status });
      await load();
    } catch {
      setError(t('support.errorUpdate'));
    }
  };

  return (
    <div style={{ padding: '30px', maxWidth: '1100px', margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Headphones size={28} color="#9333ea" />
          <div>
            <h1 style={{ margin: 0, fontSize: '26px', color: '#333' }}>{t('support.title')}</h1>
            <p style={{ margin: 0, color: '#666' }}>{t('support.ticketsSubtitle')}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', border: '1px solid #ddd', borderRadius: '8px', background: '#fff', cursor: 'pointer' }}
        >
          <RefreshCw size={16} />{t('support.refreshBtn')}</button>
      </div>

      {error && (
        <div style={{ background: '#fef2f2', color: '#b91c1c', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}>
          {error}
        </div>
      )}

      {metrics && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '24px' }}>
          {[
            { label: 'Abertos', value: metrics.open_tickets },
            { label: 'Em andamento', value: metrics.in_progress_tickets },
            { label: 'Resolvidos (mês)', value: metrics.resolved_this_month },
            { label: 'SLA estourado', value: metrics.sla_breached },
          ].map((item) => (
            <div key={item.label} style={{ background: '#fff', borderRadius: '10px', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
              <div style={{ fontSize: '22px', fontWeight: 700, color: '#9333ea' }}>{item.value}</div>
              <div style={{ fontSize: '13px', color: '#666' }}>{item.label}</div>
            </div>
          ))}
        </div>
      )}

      {sla && (
        <p style={{ color: '#666', fontSize: '14px', marginBottom: '20px' }}>
          SLA: resposta em {sla.response_hours}h · resolução em {sla.resolution_hours}h · plano {sla.plan_tier}
        </p>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: '24px', alignItems: 'start' }}>
        <div>
          <h2 style={{ fontSize: '18px', marginBottom: '12px' }}>{t('support.tickets')}</h2>
          {loading ? (
            <p>{t('common.loading')}</p>
          ) : tickets.length === 0 ? (
            <p style={{ color: '#666' }}>{t('support.noTicketsYet')}</p>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {tickets.map((ticket) => (
                <li key={ticket.id} style={{ background: '#fff', borderRadius: '10px', padding: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                    <strong>{ticket.title}</strong>
                    <span style={{ fontSize: '12px', color: '#666' }}>{STATUS_LABELS[ticket.status] || ticket.status}</span>
                  </div>
                  {ticket.description && <p style={{ margin: '8px 0 0', color: '#555', fontSize: '14px' }}>{ticket.description}</p>}
                  {isAdmin && ticket.status !== 'closed' && (
                    <select
                      value={ticket.status}
                      onChange={(e) => handleStatusChange(ticket.id, e.target.value)}
                      style={{ marginTop: '10px', padding: '6px', borderRadius: '6px', border: '1px solid #ddd' }}
                    >
                      {Object.entries(STATUS_LABELS).map(([k, v]) => (
                        <option key={k} value={k}>{v}</option>
                      ))}
                    </select>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <h2 style={{ fontSize: '18px', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Plus size={18} />{t('support.newTicketBtn')}</h2>
          <form onSubmit={handleCreate} style={{ background: '#fff', padding: '16px', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Título"
              required
              style={{ width: '100%', padding: '10px', marginBottom: '10px', borderRadius: '6px', border: '1px solid #ddd', boxSizing: 'border-box' }}
            />
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('common.description')}
              rows={4}
              style={{ width: '100%', padding: '10px', marginBottom: '10px', borderRadius: '6px', border: '1px solid #ddd', boxSizing: 'border-box' }}
            />
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              style={{ width: '100%', padding: '10px', marginBottom: '12px', borderRadius: '6px', border: '1px solid #ddd' }}
            >
              <option value="low">Baixa</option>
              <option value="medium">{t('support.average')}</option>
              <option value="high">Alta</option>
              <option value="urgent">Urgente</option>
            </select>
            <button type="submit" style={{ width: '100%', padding: '10px', background: '#9333ea', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>{t('support.openTicket')}</button>
          </form>

          {articles.length > 0 && (
            <div style={{ marginTop: '24px' }}>
              <h2 style={{ fontSize: '18px', marginBottom: '12px' }}>Base de conhecimento</h2>
              {articles.map((a) => (
                <details key={a.id} style={{ background: '#fff', marginBottom: '8px', padding: '10px', borderRadius: '8px' }}>
                  <summary style={{ cursor: 'pointer', fontWeight: 600 }}>{a.title}</summary>
                  <p style={{ marginTop: '8px', fontSize: '14px', color: '#555', whiteSpace: 'pre-wrap' }}>{a.content}</p>
                </details>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Suporte;
